import { useState, useEffect, useCallback } from "react";
import { getWorkList, getWorkTypes } from "../../../../api/hub/work";
import { BUCKETS, FALLBACK_WORK_TYPES, sortItemsByUrgency } from "../constants";
import { titleCase } from "../../../shared/format";
import { toWorkTypeOptions } from "../utils";

// fn_work_list always returns a `counts` object with ALL bucket counts, even
// when called with a `p_bucket` filter. WorkPage derives the total + every tab
// count by SUMMING `entry.counts` across every bucketCache entry — so storing
// the full counts object on each entry overcounts (the same buckets get added
// once per loaded entry). To keep that sum correct we store ONLY the entry's
// own bucket count on each cache entry: `{ [bucketKey]: count }`. The full
// counts object is still preserved in the `allCounts` state (used only by the
// bulk-delete decrement), so nothing else changes.
const singleBucketCounts = (counts, bucketKey) => {
  if (!counts || counts[bucketKey] == null) return null;
  return { [bucketKey]: counts[bucketKey] };
};

const useWorkBucketCache = ({
  clientId,
  activeBucket,
  groupBy,
  filterOwner,
  filterService,
  filterPipeline,
  clientFilter,
  includeDone,
  debouncedSearch,
  retryNonce,
  currentUserId,
}) => {
  // ─── State ────────────────────────────────────────────────────────────────
  const [bucketCache, setBucketCache] = useState({});
  const [allCounts, setAllCounts] = useState(null);
  const [listError, setListError] = useState(null);
  const [workTypes, setWorkTypes] = useState([]);
  const [workTypeOptions, setWorkTypeOptions] = useState([]);

  const cacheKey = `${activeBucket}:${groupBy}:${filterOwner || ""}:${filterService || ""}:${filterPipeline || ""}:${clientFilter || ""}:${includeDone}:${debouncedSearch || ""}`;
  // The filter signature is the cache key minus the leading bucket segment.
  // Every cache entry's key is `${bucket}:${filterSignature}`, so two entries
  // for the SAME bucket but DIFFERENT filter signatures (e.g. a stale entry
  // from the previous groupBy + a fresh one from the current groupBy) can
  // coexist in bucketCache. WorkPage sums `entry.counts` across EVERY entry,
  // so stale entries double-count bucket totals. The eviction effect below
  // uses this signature to purge entries that no longer match the active
  // filters before the fetch effects repopulate the cache.
  const filterSignature = `${groupBy}:${filterOwner || ""}:${filterService || ""}:${filterPipeline || ""}:${clientFilter || ""}:${includeDone}:${debouncedSearch || ""}`;

  // ─── Evict stale cache entries when the filter signature changes ──────────
  // Keys are `${bucket}:${filterSignature}`. When groupBy / owner / service /
  // pipeline / client / includeDone / search changes, entries keyed to the OLD
  // signature become stale. Leaving them in place makes WorkPage's sum across
  // all entries double-count (old seed counts + new seed counts). Drop every
  // entry whose key suffix doesn't match the current signature so only the
  // current filter set's entries remain — the active fetch + seed effects then
  // repopulate items and counts for the new signature.
  useEffect(() => {
    setBucketCache((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, entry]) => {
        const sig = key.slice(key.indexOf(":") + 1);
        if (sig === filterSignature) next[key] = entry;
      });
      // Only mutate state if something was actually evicted, to avoid
      // unnecessary re-renders when the signature hasn't meaningfully changed.
      if (Object.keys(next).length === Object.keys(prev).length) return prev;
      return next;
    });
  }, [filterSignature]);

  // ─── Lazy fetch active bucket ──────────────────────────────────────────────
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    if (
      !debouncedSearch &&
      bucketCache[cacheKey]?.items?.length > 0 &&
      !bucketCache[cacheKey]?.loading
    ) {
      // Rebuild the full counts from every cache entry's single-bucket count
      // (each entry now stores only its own bucket — see singleBucketCounts).
      // allCounts is the full counts object used by the bulk-delete decrement,
      // so it must stay complete even on a cache hit that skips a network call.
      const fullCounts = Object.values(bucketCache).reduce((acc, entry) => {
        if (entry?.counts) {
          Object.entries(entry.counts).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; });
        }
        return acc;
      }, {});
      if (Object.keys(fullCounts).length > 0) setAllCounts(fullCounts);
      return;
    }

    setBucketCache((prev) => ({
      ...prev,
      [cacheKey]: { loading: true, error: "", items: prev[cacheKey]?.items || [] },
    }));

    // Recurring tab (work-page-fixes Item 1): "recurring" is not a real backend
    // bucket — fn_work_list doesn't recognize it and returns zero items when
    // p_bucket is set to it. Omit p_bucket so the call returns a broad set of
    // items across all buckets; WorkPage's recurringTaskIds Set then filters
    // client-side to only the recurring open-copy tasks. All other params are
    // unchanged. The cache key still uses activeBucket ("recurring") as its
    // leading segment, so this fetch lands in its own cache entry.
    getWorkList({
      bucket: activeBucket === "recurring" ? undefined : activeBucket,
      group_by: groupBy,
      owner_id: filterOwner || undefined,
      service: filterService || undefined,
      work_type: filterPipeline || undefined,
      client_id: clientFilter || undefined,
      search: debouncedSearch || undefined,
      include_done: includeDone,
      include_subtasks: includeDone,
    })
      .then(async (resp) => {
        if (cancelled) return;
        setListError(null);
        const data = resp?.data ?? null;
        const respGroups = Array.isArray(data?.groups) ? data.groups : null;
        const allItems =
          respGroups && respGroups.length > 0
            ? respGroups.flatMap((g) => g.items || [])
            : data?.items || [];
        // Store the active items immediately so the list renders without
        // waiting on the supplementary done-bucket fetch below. p_include_done
        // is also forwarded on the call above so the main response carries the
        // global done count in data.counts (used by setAllCounts); the actual
        // done ITEMS are fetched separately because fn_work_list filters by
        // p_bucket FIRST, so done tasks never appear in a per-bucket list.
        //
        // fn_work_list returns a `counts` object with ALL bucket totals even
        // when called with a p_bucket filter, so this single active-bucket
        // response is enough to populate every tab badge. WorkPage derives the
        // tab counts by SUMMING `entry.counts` across every cache entry, so we
        // seed each bucket's entry with ONLY its own count here (see
        // singleBucketCounts). This replaces both the separate limit-1 counts
        // fetch and the non-active-bucket prefetch that used to fire on every
        // filter change (Item 2): the only network calls on a filter/bucket
        // change are now this one and, when includeDone is true, the done
        // supplement below. The most recent full counts object is also kept in
        // allCounts (setAllCounts) so it persists until the next fetch.
        const fullCounts = data?.counts || null;
        setBucketCache((prev) => {
          const next = { ...prev };
          if (fullCounts) {
            BUCKETS.forEach((b) => {
              const seedKey = `${b.key}:${filterSignature}`;
              const existing = next[seedKey] || {};
              // Only seed the count; never clobber items a real fetch already
              // stored, and never downgrade a loading/error state. (Once a
              // fetch has stored items, its counts are authoritative.)
              next[seedKey] = {
                ...existing,
                counts:
                  existing.items?.length > 0
                    ? existing.counts
                    : fullCounts[b.key] != null
                    ? { [b.key]: fullCounts[b.key] }
                    : existing.counts || null,
                loading: existing.loading ?? false,
                error: existing.error || "",
              };
            });
          }
          // Store the active items, overwriting the seeded count for the
          // active bucket with its own single-bucket count. The done supplement
          // below may later raise it to active+done when includeDone is true.
          next[cacheKey] = {
            loading: false,
            error: "",
            items: allItems,
            counts: singleBucketCounts(fullCounts, activeBucket),
          };
          return next;
        });
        if (fullCounts) setAllCounts(fullCounts);

        // ─── "Include done" supplement ───────────────────────────────────────
        // fn_work_list filters by p_bucket FIRST, so done tasks (which move to
        // bucket "done" once fn_work_move returns closed:true) NEVER appear in
        // a normal per-bucket list even when p_include_done is true — the call
        // above only returned the active bucket's active tasks. When the
        // "Include done" toggle is on, fire a SECOND fn_work_list call to
        // p_bucket "done" with the SAME owner/service/work_type/search filters
        // so only done tasks matching the current view return, then merge them
        // into this bucket's items (active first, done appended). The done
        // fetch is supplementary: on failure we keep the active items already
        // stored above and surface nothing — the main list still works.
        // NB: the cache stores a FLATTENED items array (groups are flatMapped
        // above), so the group-by case collapses to concatenation here; the
        // display re-sorts the merged list by urgency (see activeCache below).
        if (includeDone) {
          // "Mine" tab scopes tasks to the current user via the p_bucket
          // (fn_work_bucket computes "waiting_on_me" = my tasks). The
          // done-bucket fetch has no such server-side scoping — p_bucket
          // "done" returns EVERYONE's done tasks — so when viewing Mine with
          // no explicit owner filter, force p_owner_id to the current user
          // (currentUserId) so only MY done tasks return and merge into the
          // list. An explicit filterOwner always wins (respect the user's
          // filter choice); other tabs keep the existing filterOwner ||
          // undefined behavior (their p_bucket already scopes appropriately,
          // or they legitimately show everyone's done tasks).
          const doneOwnerId =
            filterOwner || (activeBucket === "waiting_on_me" ? currentUserId : null) || undefined;
          try {
            const doneResp = await getWorkList({
              bucket: "done",
              group_by: groupBy,
              owner_id: doneOwnerId,
              service: filterService || undefined,
              work_type: filterPipeline || undefined,
              client_id: clientFilter || undefined,
              search: debouncedSearch || undefined,
              include_done: includeDone,
              include_subtasks: includeDone,
            });
            if (cancelled) return;
            const doneData = doneResp?.data ?? null;
            const doneGroups = Array.isArray(doneData?.groups) ? doneData.groups : null;
            const doneItems =
              doneGroups && doneGroups.length > 0
                ? doneGroups.flatMap((g) => g.items || [])
                : doneData?.items || [];
            // Fold the filtered done items into the active items. COUNTS: store
            // ONLY the active bucket's own count on this entry (NOT own + done).
            // Baking done into entry.counts was the root cause of cross-bucket
            // accumulation (work-page-fixes §B): every visited bucket's entry
            // retained the merged own+done value, and WorkPage sums entry.counts
            // across every entry — adding done once per visited bucket (4 tabs ×
            // 25k = 70k+). With done kept out of `counts`, that sum is always
            // own-only per bucket. The filtered done count is stored separately
            // in `doneCount` so WorkPage can derive the active tab's display as
            // ownCount + doneCount at read time (never accumulated). Use the
            // done-bucket call's own filtered done count (doneData.counts.done)
            // — NOT the main response's global counts.done, which ignores the
            // owner/service/search filters and would over/under-count.
            const activeCount = data?.counts?.[activeBucket] ?? allItems.length;
            const doneCount = doneData?.counts?.done ?? doneItems.length;
            setBucketCache((prev) => ({
              ...prev,
              [cacheKey]: {
                loading: false,
                error: "",
                items: allItems.concat(doneItems),
                counts: { [activeBucket]: activeCount },
                doneCount,
              },
            }));
          } catch {
            // Done-bucket fetch failed — supplementary, so swallow it. The
            // active items stored above remain; do not raise a list error.
          }
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setListError(e?.message || "Failed to load work items");
        setBucketCache((prev) => ({
          ...prev,
          [cacheKey]: { loading: false, error: e?.message || "Failed to load", items: [] },
        }));
      });

    return () => { cancelled = true; };
  }, [clientId, activeBucket, groupBy, filterOwner, filterService, filterPipeline, clientFilter, includeDone, debouncedSearch, retryNonce]);

  // ─── Load work types ────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    getWorkTypes()
      .then((resp) => { if (!cancelled) setWorkTypes(resp?.data || []); })
      .catch(() => { if (!cancelled) setWorkTypes(FALLBACK_WORK_TYPES); });
    return () => { cancelled = true; };
  }, []);

  // ─── Work type filter options (ops_work_types preferred, bucketCache fallback)
  useEffect(() => {
    if (workTypes.length > 0) {
      setWorkTypeOptions(toWorkTypeOptions(workTypes));
      return;
    }

    // Fallback: extract unique work_type values from bucketCache items.
    const seen = new Set();
    const values = [];
    Object.values(bucketCache).forEach((entry) => {
      (entry?.items || []).forEach((item) => {
        const raw = item?.work_type;
        if (raw == null || seen.has(raw)) return;
        seen.add(raw);
        values.push(raw);
      });
    });
    values.sort((a, b) => String(a).localeCompare(String(b)));
    setWorkTypeOptions(
      values.map((raw) => ({
        value: raw,
        label: titleCase(String(raw).replace(/_/g, " ")),
      }))
    );
  }, [workTypes, bucketCache]);

  // ─── Derived: sorted active cache ──────────────────────────────────────────
  const rawCache = bucketCache[cacheKey] || { loading: true, items: [] };
  const activeCache = {
    ...rawCache,
    items: (rawCache.items || []).slice().sort(sortItemsByUrgency),
  };

  // ─── Cache mutation helpers ───────────────────────────────────────────────
  // Patch a single item across every cache entry (status change, title edit,
  // due-date change, owner reassign, etc.). Eliminates the 8-line
  // setBucketCache boilerplate that was repeated in 10+ handlers.
  const patchItem = useCallback((taskId, updates) => {
    setBucketCache((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (next[key]?.items) {
          next[key] = {
            ...next[key],
            items: next[key].items.map((i) => (i.id === taskId ? { ...i, ...updates } : i)),
          };
        }
      });
      return next;
    });
  }, []);

  // Remove a single item from every cache entry.
  const removeItem = useCallback((taskId) => {
    setBucketCache((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (next[key]?.items) {
          next[key] = { ...next[key], items: next[key].items.filter((i) => i.id !== taskId) };
        }
      });
      return next;
    });
  }, []);

  // Remove multiple items from every cache entry AND update allCounts.
  // Used by bulk-delete, bulk-archive, and undo-restore flows.
  const removeItems = useCallback((ids) => {
    const idSet = new Set(ids);
    const removedByBucket = {};
    setBucketCache((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (!next[key]?.items) return;
        const remaining = next[key].items.filter((i) => !idSet.has(i.id));
        const removedHere = next[key].items.length - remaining.length;
        if (removedHere > 0) {
          const bucketKey = key.split(":")[0];
          removedByBucket[bucketKey] = (removedByBucket[bucketKey] || 0) + removedHere;
        }
        next[key] = { ...next[key], items: remaining };
      });
      return next;
    });
    if (Object.keys(removedByBucket).length > 0) {
      setAllCounts((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        Object.keys(removedByBucket).forEach((bk) => {
          next[bk] = Math.max(0, (next[bk] || 0) - removedByBucket[bk]);
        });
        return next;
      });
    }
  }, []);

  return {
    bucketCache,
    setBucketCache,
    cacheKey,
    allCounts,
    setAllCounts,
    listError,
    setListError,
    activeCache,
    workTypes,
    workTypeOptions,
    patchItem,
    removeItem,
    removeItems,
  };
};

export default useWorkBucketCache;
